import axios from 'axios';
import {
    getAccessToken,
    setAccessToken,
    getRefreshToken,
    setRefreshToken,
    clearAuthSession,
} from './authSession';

import { API_BASE_URL } from './apiOrigin';

/**
 * Pre-configured Axios instance with:
 * - Base URL from environment
 * - JSON content type
 * - No HTTP caching (avoids stale API responses from intermediaries)
 * - Auth token injection
 * - Token refresh on 401
 * - Standardized error responses
 */
const apiClient = axios.create({
    baseURL: API_BASE_URL,
    timeout: 30000,
    headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        'Cache-Control': 'no-cache',
        Pragma: 'no-cache',
    },
});

// ─── Request Interceptor ────────────────────────────────────────────────────
apiClient.interceptors.request.use(
    (config) => {
        const token = getAccessToken();
        if (token) {
            config.headers.Authorization = `Bearer ${token}`;
        } else {
            const hasExplicitBearer =
                config.headers?.Authorization ||
                config.headers?.authorization;
            // Caller may set Authorization after storage was cleared (e.g. server logout with saved token).
            if (!hasExplicitBearer) {
                // apiClient may still have a default Authorization from a prior refresh/login.
                if (config?.headers) {
                    if (typeof config.headers.delete === 'function') {
                        config.headers.delete('Authorization');
                    } else {
                        delete config.headers.Authorization;
                        delete config.headers.authorization;
                    }
                }
            }
        }
        // Default Content-Type is application/json; multipart uploads need the browser to set
        // boundary (multer expects multipart/form-data with a part named `file`).
        if (typeof FormData !== 'undefined' && config.data instanceof FormData) {
            if (typeof config.headers.delete === 'function') {
                config.headers.delete('Content-Type');
            } else {
                delete config.headers['Content-Type'];
            }
        }
        // Ensure no-cache on every request (callers may merge custom headers)
        config.headers['Cache-Control'] = 'no-cache';
        config.headers.Pragma = 'no-cache';
        return config;
    },
    (error) => Promise.reject(error)
);

// ─── Response Interceptor ───────────────────────────────────────────────────
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
    failedQueue.forEach((prom) => {
        if (error) {
            prom.reject(error);
        } else {
            prom.resolve(token);
        }
    });
    failedQueue = [];
};

apiClient.interceptors.response.use(
    (response) => response,
    async (error) => {
        const originalRequest = error.config;

        // Skip refresh logic for auth endpoints
        if (originalRequest.url?.includes('/auth/')) {
            return Promise.reject(error);
        }

        // Temporary password still in use: the API refuses everything until it is changed.
        if (
            error.response?.status === 403 &&
            error.response.data?.reason === 'PASSWORD_CHANGE_REQUIRED' &&
            window.location.pathname !== '/change-password'
        ) {
            window.location.href = '/change-password';
            return Promise.reject(error);
        }

        // Handle 401 — attempt token refresh
        if (error.response?.status === 401 && !originalRequest._retry) {
            if (isRefreshing) {
                return new Promise((resolve, reject) => {
                    failedQueue.push({ resolve, reject });
                })
                    .then((token) => {
                        originalRequest.headers.Authorization = `Bearer ${token}`;
                        return apiClient(originalRequest);
                    })
                    .catch((err) => Promise.reject(err));
            }

            originalRequest._retry = true;
            isRefreshing = true;

            try {
                const refreshToken = getRefreshToken();
                if (!refreshToken || typeof refreshToken !== 'string') {
                    processQueue(error, null);
                    clearAuthSession();
                    window.location.href = '/?login=1';
                    return Promise.reject(error);
                }
                const { data } = await axios.post(`${API_BASE_URL}/auth/refreshToken`, {
                    refreshToken,
                });

                const newAccess = data.access?.token;
                const nextRefresh = data.refresh?.token;
                if (!newAccess) {
                    throw new Error('Invalid refresh response');
                }

                setAccessToken(newAccess);
                if (nextRefresh) {
                    setRefreshToken(nextRefresh);
                }

                apiClient.defaults.headers.common.Authorization = `Bearer ${newAccess}`;
                processQueue(null, newAccess);

                originalRequest.headers.Authorization = `Bearer ${newAccess}`;
                return apiClient(originalRequest);
            } catch (refreshError) {
                processQueue(refreshError, null);
                clearAuthSession();
                window.location.href = '/?login=1';
                return Promise.reject(refreshError);
            } finally {
                isRefreshing = false;
            }
        }

        // Normalize error shape.
        //
        // 413 is special: the reverse proxy rejects an oversized body before the API
        // ever runs, so it answers with an HTML error page and `data.message` is
        // undefined — which left the UI showing the raw
        // "Request failed with status code 413". Callers that know their own limits
        // (see uploadEvidenceFiles) replace this with something more specific.
        const status = error.response?.status || 500;
        const fallbackMessage =
            status === 413
                ? 'The upload is too large for the server to accept. Attach fewer or smaller files and try again.'
                : error.message || 'An unexpected error occurred';

        const normalizedError = {
            message: error.response?.data?.message || fallbackMessage,
            status,
            data: error.response?.data || null,
        };

        return Promise.reject(normalizedError);
    }
);

export default apiClient;
